import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { useLocalizeMessage, Badge, Link, useDateTimeFormat } from 'libs.nucleus';
import { statusLabelMap, statusMap } from './participant_documents.status';
import { SiteContext } from '../../contexts/site';
import { getApiInstance } from '../../helpers/api.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useOrgContext } from '../../hooks/use_org_context';
import { useTranslateDisplayName } from '../../hooks/use_translate_display_name';
/**
 * A plain primary-button click with no modifier keys. Modified clicks
 * (⌘/ctrl/alt/shift, non-left button) or an already-handled event should fall
 * through to the browser's native new-tab navigation rather than open the modal.
 */
const isUnmodifiedPrimaryClick = (event) => event.button === 0 && !event.defaultPrevented && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey;
export const useParticipantDocumentsTableColumns = ({ getDocumentHref, openDocumentsInModal, onOpenDocument, isPtsInteractionEnabled = false, onOpenPtsDocument, }) => {
    const translate = useLocalizeMessage();
    const { formatDateTime } = useDateTimeFormat();
    const participantDocumentsColumns = useMemo(() => {
        return [
            {
                header: translate('generic.title'),
                accessorKey: 'ec__document_template.ec__title',
                cell: (context) => {
                    const title = context.getValue();
                    const document = context.row.original;
                    const documentHref = getDocumentHref(document);
                    // Print-to-sign is an attribute of the document, not a status, so the
                    // badge sits next to the template name rather than in the Status column.
                    const showPtsBadge = isPtsInteractionEnabled && !!document.ec__pts;
                    // PTS rows open the in-app modal instead of deep-linking to classic.
                    // The href is preserved as a graceful fallback if JS is disabled.
                    const titleLink = isPtsInteractionEnabled && document.ec__pts && onOpenPtsDocument ? (_jsx(Link, { href: documentHref || undefined, "data-testid": 'pts-document-link', onClick: (event) => {
                            event.preventDefault();
                            onOpenPtsDocument(document);
                        }, children: title })) : (_jsx(Link, { href: documentHref || undefined, target: '_blank', rel: 'noreferrer', onClick: (event) => {
                            if (!openDocumentsInModal || !isUnmodifiedPrimaryClick(event)) {
                                return;
                            }
                            event.preventDefault();
                            onOpenDocument(document);
                        }, children: title }));
                    return (_jsxs("div", { className: 'flex items-center gap-2', children: [titleLink, showPtsBadge && _jsx(Badge, { variant: 'common', label: translate('label.participant_documents.pts_badge') })] }));
                },
            },
            {
                header: translate('label.participant_documents.required_signers'),
                accessorKey: 'ec__required_signers',
            },
            {
                header: translate('generic.create', { tense: 'past' }),
                accessorKey: 'created',
                cell: (context) => formatDateTime(context.getValue()),
            },
            {
                header: translate('generic.status'),
                accessorKey: 'ec__status',
                cell: (context) => {
                    const status = context.getValue();
                    return _jsx(Badge, { variant: statusMap[status], label: translate(statusLabelMap[status]) });
                },
            },
        ];
    }, [
        formatDateTime,
        getDocumentHref,
        isPtsInteractionEnabled,
        onOpenDocument,
        onOpenPtsDocument,
        openDocumentsInModal,
        translate,
    ]);
    return { participantDocumentsColumns };
};
const fetchParticipantDocuments = async (subject, apiInstance, accountId, siteId) => {
    if (!subject.id) {
        return [];
    }
    const response = await apiInstance.get(`/v2/account/${accountId}/c_sites/${siteId}/c_subjects/${subject.id}/ec__documents`, {
        params: {
            where: JSON.stringify({ ec__site: siteId }),
            paths: ['ec__document_template.ec__title'],
            include: [
                'ec__status',
                'ec__required_signers',
                'created',
                'ec__pts',
                'ec__pts_signature_date_wallclock',
                // File property — included so Cortex resolves a download `url` for the scanned document.
                'ec__final_document',
            ],
        },
    });
    if (!response?.data?.data) {
        return [];
    }
    return response.data.data.map((document) => {
        if (!subject.displayName) {
            return document;
        }
        return {
            ...document,
            ec__document_template: {
                ...document.ec__document_template,
                ec__title: `${subject.displayName} • ${document.ec__document_template.ec__title}`,
            },
        };
    });
};
export const useParticipantDocuments = (participant) => {
    const account = useAxonAccountContext();
    const { site } = useContext(SiteContext);
    const { environment } = useCurrentEnvironment();
    const org = useOrgContext();
    const translateDisplayName = useTranslateDisplayName();
    const isCaregiverFeatureEnabled = org?.c_caregiver_enabled ?? false;
    const getParticipantDocumentsData = async () => {
        if (!environment) {
            return [];
        }
        try {
            const apiInstance = getApiInstance(environment);
            const subjects = [
                { id: participant?._id, displayName: undefined },
                ...(isCaregiverFeatureEnabled ? participant?.c_caregiver_relationship?.c_caregivers_info || [] : []).map(({ c_public_user, c_caregiver_display_name }) => ({
                    id: c_public_user?._id,
                    displayName: translateDisplayName(c_caregiver_display_name),
                })),
            ].filter(({ id }) => !!id);
            const documentsPromises = subjects.map((subject) => fetchParticipantDocuments(subject, apiInstance, account?._id, site?._id));
            const results = await Promise.allSettled(documentsPromises);
            return results
                .filter((result) => result.status === 'fulfilled')
                .flatMap((result) => result.value);
        }
        catch (error) {
            return [];
        }
    };
    return useQuery({
        queryKey: ['participantDocuments', participant?._id],
        queryFn: getParticipantDocumentsData,
        enabled: !!participant?._id,
    });
};
